import { Result } from "better-result";
import { createServerFn } from "@tanstack/react-start";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { redirect } from "@tanstack/react-router";
import { z } from "zod/v4";
import { apiEnum } from "#/modules/util.helpers/zod-enum";
import {
  createRapport,
  deleteRapport,
  getClassesForReport,
  getRapport,
  getRapportLeerlingResultaten,
  getRapportNotities,
  getRapporten,
  updateRapportElementen,
  updateRapportFilter,
  updateRapportKlassen,
  updateRapportNotitie,
  updateRapportPeriod,
} from "#/modules/rapporten/api.rapporten.server";
import { apiFailure } from "#/modules/util.errors/api-failure.server";
import { type ApiFailure } from "#/modules/util.errors/translate";
import { rapportenOverviewSearchSchema } from "#/modules/rapporten/params.rapporten";
import { getLeerlingNotities } from "#/modules/leerling.notities/api.leerling-notities.server";
import type { NotitiesSearch } from "#/modules/student/schema.search.notities";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";
import type { components } from "#/schema";
import { RapportElementen } from "#/schema.nswag";

const RAPPORTEN_OVERVIEW_PAGE_SIZE = 1000;

export const getReportsLayoutData = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async () => {
    const result = await getClassesForReport();
    if (Result.isError(result)) {
      throw result.error;
    }
    return { klassen: result.value?.data ?? [] };
  });

export const getReportsOverview = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(rapportenOverviewSearchSchema)
  .handler(async ({ data }) => {
    const result = await getRapporten({
      page: 1,
      pageSize: RAPPORTEN_OVERVIEW_PAGE_SIZE,
      ...data,
    });

    if (Result.isError(result)) {
      throw result.error;
    }

    return {
      rapporten: result.value?.data ?? [],
      totalCount: result.value?.count ?? 0,
    };
  });

export const getRapportFlowData = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(z.object({ rapportId: z.string() }))
  .handler(async ({ data }) => {
    const result = await getRapport(data.rapportId);
    if (Result.isError(result)) {
      throw result.error;
    }
    return { rapport: result.value };
  });

export const getRapportStep3Notities = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(z.object({ rapportId: z.string() }))
  .handler(async ({ data }) => {
    const result = await getRapportNotities(data.rapportId);
    if (Result.isError(result)) {
      throw result.error;
    }
    return { notities: result.value?.groupedNotities ?? [] };
  });

export const getRapportStep4Data = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(z.object({ rapportId: z.string() }))
  .handler(async ({ data }) => {
    const result = await getRapportLeerlingResultaten(data.rapportId);
    if (Result.isError(result)) {
      throw result.error;
    }
    return { rapportLeerlingResultaten: result.value };
  });

export const getRapportDrawerNotities = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: { rapportId: string; leerlingId: string } & NotitiesSearch) => data)
  .handler(async ({ data }) => {
    const rapportResult = await getRapport(data.rapportId);
    if (Result.isError(rapportResult)) {
      throw rapportResult.error;
    }
    const rapport = rapportResult.value;

    const notitiesResult = await getLeerlingNotities({
      excludeRapportId: data.rapportId,
      leerlingId: data.leerlingId,
      searchTerm: data.searchTerm,
      notitieTypes: data.notitieTypes,
      includeFullHistory: data.includeFullHistory,
      page: data.page,
      from: rapport?.from ?? data.from,
      to: rapport?.to ?? data.to,
      afgerondAtFrom: "",
      afgerondAtTo: "",
      pageSize: 100,
    });

    if (Result.isError(notitiesResult)) {
      throw notitiesResult.error;
    }

    return {
      notities: notitiesResult.value,
      rapportFrom: rapport?.from,
      rapportTo: rapport?.to,
    };
  });

const createRapportSchema = z.object({
  from: z.string(),
  to: z.string(),
  klassen: z.array(z.string()),
  name: z.string().nullable(),
});

// The caller navigates itself (typed): step 3 of the flow when a klas is known, step 2 otherwise.
type CreateRapportResult = { rapportId: string; firstKlasId?: string };

export const submitCreateRapport = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(createRapportSchema)
  .handler(async ({ data }): Promise<CreateRapportResult | ApiFailure> => {
    const filters: components["schemas"]["CreateRapportCommand"] = {
      from: data.from,
      to: data.to,
      klassen: data.klassen,
      name: data.name,
    };

    const createdResult = await createRapport(filters);
    if (Result.isError(createdResult)) {
      return apiFailure(createdResult.error);
    }
    const created = createdResult.value;

    const rapportResult = await getRapport(created.id);
    if (Result.isError(rapportResult)) {
      return apiFailure(rapportResult.error);
    }

    const firstKlas = rapportResult.value?.klassen?.[0];
    return { rapportId: created.id, firstKlasId: firstKlas?.id };
  });

export const submitDeleteRapport = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(z.object({ rapportId: z.string() }))
  .handler(async ({ data }) => {
    const result = await deleteRapport(data.rapportId);
    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });

export const submitUpdateRapportNotitie = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(
    z.object({
      rapportId: z.string(),
      leerlingId: z.string(),
      notitie: z.string().nullable(),
    }),
  )
  .handler(async ({ data }) => {
    const result = await updateRapportNotitie({
      rapportId: data.rapportId,
      leerlingId: data.leerlingId,
      notitie: data.notitie,
    });
    if (Result.isError(result)) return apiFailure(result.error);
    return { success: true };
  });

export const submitUpdateRapportName = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(z.object({ rapportId: z.string(), name: z.string().nullable() }))
  .handler(async ({ data }) => {
    const rapportResult = await getRapport(data.rapportId);
    if (Result.isError(rapportResult)) {
      return apiFailure(rapportResult.error);
    }
    const rapport = rapportResult.value;

    const result = await updateRapportPeriod({
      rapportId: data.rapportId,
      from: rapport.from,
      to: rapport.to,
      name: data.name?.trim() ? data.name.trim() : null,
    });
    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });

const updatePeriodSchema = z.object({
  locale: z.string(),
  schoolyear: z.string().optional(),
  school: z.string(),
  rapportId: z.string(),
  from: z.string(),
  to: z.string(),
  name: z.string(),
  intent: z.enum(["update", "proceed"]),
});

export const submitUpdateRapportPeriod = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(updatePeriodSchema)
  .handler(async ({ data }) => {
    const result = await updateRapportPeriod({
      rapportId: data.rapportId,
      from: data.from,
      to: data.to,
      name: data.name,
    });
    if (Result.isError(result)) {
      return apiFailure(result.error);
    }

    if (data.intent === "update") {
      return;
    }

    throw redirect({
      to: "/$locale/{-$schoolyear}/$school/reports/flow/$id/step/2",
      params: {
        locale: data.locale,
        schoolyear: data.schoolyear,
        school: data.school,
        id: data.rapportId,
      },
    });
  });

const updateKlassenSchema = z.object({
  locale: z.string(),
  schoolyear: z.string().optional(),
  school: z.string(),
  rapportId: z.string(),
  klassen: z.array(z.string()),
  intent: z.enum(["update", "proceed"]),
});

export const submitUpdateRapportKlassen = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(updateKlassenSchema)
  .handler(async ({ data }) => {
    const rapportResult = await getRapport(data.rapportId);
    if (Result.isError(rapportResult)) return apiFailure(rapportResult.error);
    const rapport = rapportResult.value;
    const result = await updateRapportKlassen({
      rapportId: data.rapportId,
      klassen: data.klassen,
    });
    if (Result.isError(result)) {
      return apiFailure(result.error);
    }

    if (data.intent === "update") {
      return;
    }

    // Mirrors rapporten-flow.steps.tsx's step-3 `href`: no (first) klas yet -> back to step 2,
    // otherwise straight to that klas's notities tab on step 3.
    const hasKlassen = rapport?.klassen !== undefined && rapport.klassen.length > 0;
    const firstKlas = hasKlassen ? rapport.klassen[0] : undefined;

    if (firstKlas) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/$school/reports/flow/$id/step/3/$class",
        params: {
          locale: data.locale,
          schoolyear: data.schoolyear,
          school: data.school,
          id: data.rapportId,
          class: firstKlas.id,
        },
      });
    }

    throw redirect({
      to: "/$locale/{-$schoolyear}/$school/reports/flow/$id/step/2",
      params: {
        locale: data.locale,
        schoolyear: data.schoolyear,
        school: data.school,
        id: data.rapportId,
      },
    });
  });

const updateElementenSchema = z.object({
  rapportId: z.string(),
  rapportElementen: z.array(apiEnum(RapportElementen)),
  leerlijnFilter: z.array(z.string()),
  sociaalEmotioneelDomeinFilter: z.array(z.string()),
});

export const submitUpdateRapportElementen = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(updateElementenSchema)
  .handler(async ({ data }) => {
    const filterResult = await updateRapportFilter({
      rapportId: data.rapportId,
      filterSelection: {
        leerlijnSelectie: {
          selectedLeerlijnFilterIds: data.leerlijnFilter,
        },
        domeinSelectie: {
          selectedDomeinFilterIds: data.sociaalEmotioneelDomeinFilter,
        },
      },
    });
    if (Result.isError(filterResult)) {
      return apiFailure(filterResult.error);
    }

    const result = await updateRapportElementen({
      rapportId: data.rapportId,
      rapportElementen: data.rapportElementen,
    });
    if (Result.isError(result)) {
      return apiFailure(result.error);
    }

    return { rapportElementen: data.rapportElementen };
  });
